import { WORLD } from '../core/config';
import { layoutToWorld } from '../core/coords';
import type { Layout } from '../data/types';
import type { Vec2 } from '../geo/types';
import yard from '../data/lastageYard.json';
import goods from '../data/quayGoods.json';
import type { SoundEmitter, SoundKind } from './spatialAudio';

function emitter(
  id: string,
  kind: SoundKind,
  at: Vec2,
  reach: number,
  level: number,
  height = 1
): SoundEmitter {
  return { id, kind, at: layoutToWorld(at, height), reach, level };
}

/** Locations follow the model's evidence. Activity, timing, loudness and audible radii are art direction. */
export function createAcousticScene(layout: Layout): readonly SoundEmitter[] {
  const sources: SoundEmitter[] = layout.water.channels.map((channel) => ({
    ...emitter(
      channel.id,
      'water',
      channel.centerline[0],
      Math.max(...channel.widths) / 2 + 20,
      channel.kind === 'ditch' ? 0.06 : 0.14,
      WORLD.waterY
    ),
    path: channel.centerline,
  }));
  for (const mooring of layout.harbour.moorings) {
    const count = mooring.craft.ship + mooring.craft.smallCraft + mooring.craft.unmasted;
    if (count === 0) continue;
    sources.push({
      ...emitter(mooring.id, 'rigging', mooring.path[0], 48, 0.12, 0),
      path: mooring.path,
    });
  }
  for (const berth of layout.harbour.berths) {
    sources.push({
      ...emitter(berth.id, 'rigging', berth.pos, 65, 0.13, 1),
      canvas: berth.sailSet,
    });
  }
  for (const building of layout.landmarks) {
    if (building.id === 'landmark-oude-kerk' || building.id === 'landmark-nieuwe-kerk')
      sources.push(emitter(building.id, 'bell', building.pos, 1800, 0.38, 24));
    if (building.kind === 'crane')
      sources.push(emitter(building.id, 'cargo', building.pos, 60, 0.18));
    if (building.id === 'landmark-vismarkt')
      sources.push(emitter(building.id, 'market', building.pos, 45, 0.13));
  }
  for (const item of yard.items) {
    const [x, y] = item.at;
    if (x === undefined || y === undefined) continue;
    if (item.kind === 'plankPile' || item.kind === 'beamHeap' || item.kind === 'logStack')
      sources.push(emitter(`yard-${item.id}`, 'carpentry', [x, y], 65, 0.16));
    if (item.kind === 'boxStack')
      sources.push(emitter(`yard-${item.id}`, 'cargo', [x, y], 40, 0.13));
  }
  for (const item of goods.items) {
    const [x, y] = item.at;
    if (x === undefined || y === undefined) continue;
    if (item.kind === 'boxStack' || item.kind === 'crate')
      sources.push(emitter(`quay-${item.id}`, 'cargo', [x, y], 35, 0.11));
  }
  return sources;
}
